import classnames from 'clsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { player } from 'src/client/player';
import { PLAYER_MANAGER_ENABLED } from 'src/common/constants/experiments/config';
import useActions from 'src/common/hooks/useActions';
import useExperiment from 'src/common/hooks/useExperiment';
import { useOutsideClickControl } from 'src/common/hooks/useOutsideClickControl';
import { useStores } from 'src/common/providers';
import { selectIsMobile } from 'src/common/selectors/app';
import { selectPlaybackRate } from 'src/common/selectors/player';
import { changePlaybackRateIfPossible } from '../../../actions/tuner';
import Popover from '../../shared/Popover';
import PlayBackRateOption from './PlayBackRateOption';
import css from './speed-control.module.scss';

const playbackRateOptions = [0.5, 0.8, 1.0, 1.2, 1.5, 2.0, 3.0];
const mobilePlaybackRateOptions = [0.5, 1.0, 1.5, 2.0];

export default function SpeedControl({ iconContainerClass }) {
  const legacyPlaybackRate = useSelector(selectPlaybackRate);
  const actions = useActions({ changePlaybackRateIfPossible });
  const isPlayerManagerEnabled = useExperiment(PLAYER_MANAGER_ENABLED);
  const { playerStore } = useStores();
  const playerManagerPlaybackRate = playerStore.playbackRate();
  const playbackRate = isPlayerManagerEnabled
    ? playerManagerPlaybackRate
    : legacyPlaybackRate;
  const { handleOnClick, isOpen, containerRef } = useOutsideClickControl();
  const isMobile = useSelector(selectIsMobile);

  if (isMobile) {
    const handleMobileOnClick = () => {
      const currentIndex = mobilePlaybackRateOptions.indexOf(playbackRate);
      const nextPlaybackRate =
        mobilePlaybackRateOptions[currentIndex + 1] ||
        mobilePlaybackRateOptions[0];

      player.isActive
        ? player.setPlaybackRate(nextPlaybackRate)
        : actions.changePlaybackRateIfPossible(nextPlaybackRate);
    };

    return (
      <div className={iconContainerClass} onClick={handleMobileOnClick}>
        <div
          className={classnames(
            css.speedControlText,
            css.mobileSpeedControlText,
          )}
        >
          {`${playbackRate}x`}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <button
        type="button"
        className={iconContainerClass}
        onClick={handleOnClick}
      >
        <div className={css.speedControlText}>{`${playbackRate}x`}</div>
      </button>
      <Popover isOpen={isOpen} className={css.popover}>
        <div className={css.content}>
          {playbackRateOptions.map((val) => (
            <PlayBackRateOption
              key={val}
              value={val}
              className={css.row}
              dataTestId={`rate-${val}`}
            />
          ))}
        </div>
      </Popover>
    </div>
  );
}

SpeedControl.propTypes = {
  iconContainerClass: PropTypes.string,
  playbackRate: PropTypes.number,
  handleOnClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  containerRef: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
};
